import { useCallback } from 'react';

import {
  sumLedgerTotal,
  useCheckoutBrazeLogPurchase,
} from '@order/features/ordering';
import { type OrderingContext } from '@order/features/ordering';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { useBraze } from '../../../../context/Braze';
import { useSnapPixel } from '../../../../integrations';
import { getCheckoutTelemetryData } from '../../utils';

export const usePostOrderTracking = () => {
  const { track } = useTelemetry();
  const { trackPurchase: trackSnapPixelPurchase } = useSnapPixel();
  const { logPurchase } = useBraze();
  const logBrazePurchase = useCheckoutBrazeLogPurchase({ logPurchase });

  const isSkipPostOrderTrackingEnabled = useFeatureFlag(
    'CELS-4828-skip-post-order-tracking-enabled',
  );

  const handlePostOrderTracking = useCallback(
    (context: OrderingContext) => {
      const {
        orderId = '',
        cart,
        selectedTip,
        availableCredit,
        shouldUseCredit,
      } = context;

      const {
        restaurantId = '',
        orderChannel = 'pickup',
        lineItems,
        ledger,
      } = cart;

      const { total, subtotal, discountTotal } = sumLedgerTotal(
        ledger,
        selectedTip,
        availableCredit,
        shouldUseCredit,
      );

      trackSnapPixelPurchase({
        priceInDollars: total / 100,
        orderId,
        productIds: lineItems?.map((item) => item.product?.id ?? '') ?? [],
        lineItemsQuantity: lineItems?.length ?? 0,
      });

      if (isSkipPostOrderTrackingEnabled) {
        return;
      }

      track('checkout.success', getCheckoutTelemetryData(context));

      logBrazePurchase({
        restaurantId,
        lineItems,
        orderChannel,
        total,
        subtotal,
        discountTotal,
      });
    },
    [
      isSkipPostOrderTrackingEnabled,
      logBrazePurchase,
      track,
      trackSnapPixelPurchase,
    ],
  );

  // ─────────────────────────────────────────────────────────────────────

  return { handlePostOrderTracking };
};
